import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { useAuth } from "./contexts/AuthContext";
import NotFound from "pages/NotFound";
import AdminDashboard from './pages/admin-dashboard';
import TournamentDetails from './pages/tournament-details';
import UserDashboard from './pages/user-dashboard';
import MatchPredictions from './pages/match-predictions';
import ResultsManagement from './pages/results-management';
import TournamentManagement from './pages/tournament-management';
import WinnerAnnouncement from './pages/winner-announcement';
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import ResetPassword from './pages/auth/ResetPassword';
import TournamentsList from './pages/tournaments-list';
import GlobalTeamManagement from './pages/global-team-management';
import TournamentSquadManagement from './pages/tournament-squad-management';
import UserManagement from './pages/user-management';
import Notifications from './pages/notifications';
import AnalyticsDashboard from './pages/analytics-dashboard';
import PointsVerification from './pages/points-verification';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, userProfile, loading, isEmailVerified } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  if (!isEmailVerified()) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Email Verification Required</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Please verify your email address to access this page. Check your inbox for the verification link.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            After clicking the verification link, you may be redirected to localhost. This is normal - just navigate back here to continue.
          </p>
        </div>
      </div>
    );
  }

  if (adminOnly && userProfile?.role !== 'admin') {
    return <Navigate to="/user-dashboard" replace />;
  }

  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        {/* Public Routes */}
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Protected User Routes */}
        <Route path="/" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
        <Route path="/user-dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
        <Route path="/tournament-details/:tournamentId" element={<ProtectedRoute><TournamentDetails /></ProtectedRoute>} />
        <Route path="/match-predictions/:matchId" element={<ProtectedRoute><MatchPredictions /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        <Route path="/analytics-dashboard" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/points-verification" element={<ProtectedRoute><PointsVerification /></ProtectedRoute>} />
        
        {/* Protected Admin Routes */}
        <Route path="/admin-dashboard" element={<ProtectedRoute adminOnly={true}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/tournament-management/:tournamentId" element={<ProtectedRoute adminOnly={true}><TournamentManagement /></ProtectedRoute>} />
        <Route path="/global-team-management" element={<ProtectedRoute adminOnly={true}><GlobalTeamManagement /></ProtectedRoute>} />
        <Route path="/tournament-squad-management" element={<ProtectedRoute adminOnly={true}><TournamentSquadManagement /></ProtectedRoute>} />
        <Route path="/results-management" element={<ProtectedRoute adminOnly={true}><ResultsManagement /></ProtectedRoute>} />
        <Route path="/user-management" element={<ProtectedRoute adminOnly={true}><UserManagement /></ProtectedRoute>} />
        <Route path="/winner-announcement/:tournamentId" element={<ProtectedRoute adminOnly={true}><WinnerAnnouncement /></ProtectedRoute>} />
        <Route path="/tournaments-list" element={<ProtectedRoute adminOnly={true}><TournamentsList /></ProtectedRoute>} />
        
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
